import React, { useCallback } from 'react';
import { PopoverDisclosure, usePopoverState } from 'reakit/Popover';
import {
  CorpIotTeamDevicesType,
  corporateDeviceTypeOptions,
  corporateRegionsOptions,
  editingOptions,
} from '../../../../constants/app';
import styles from '../../WifiConnect.module.scss';
import { FormState } from '../Forms/Form';
import { Actions } from './Actions';
import { Device } from './DevicesTable';
import { FormattedMessage } from 'react-intl';
import { getRegion, getType } from '../../../../utils/generators';

interface DeviceTableRowProps {
  device: Device;
  onEdit: (mac: string, device: FormState, onCancel?: () => void) => void;
  onDelete: (mac: string, name: string, onCancel?: () => void) => void;
  hasAccessToCorpDevices: boolean;
  onConfirm: Function;
}

const DeviceTableRow: React.FC<DeviceTableRowProps> = ({
  device,
  onEdit,
  onDelete,
  hasAccessToCorpDevices,
  onConfirm,
}) => {
  const popover = usePopoverState({ placement: 'top-start' });

  const actions = {
    onEdit: () => {
      onEdit(device.mac, makeDeviceFormState());
    },
    onDelete: () => {
      onDelete(device.mac, device.name);
    },
    onConfirm: () => {
      onConfirm(device.mac, device.name, makeDeviceFormState());
    },
  };

  const makeDeviceFormState = useCallback(
    () => ({
      mac: device.mac,
      name: device.name,
      type: editingOptions.includes(device.type)
        ? device.type
        : 'Other device (including ancillary device used for IoT setup)',
      otherType: !editingOptions.includes(device.type) ? device.type : '',
      location: device.location_uuid,
      region: corporateRegionsOptions.includes(getRegion(device.name))
        ? getRegion(device.name)
        : '',
      corporateType: corporateDeviceTypeOptions.includes(getType(device.name))
        ? getType(device.name)
        : '',
      notes: device.notes,
      COMPANY_ID: device.COMPANY_ID,
      COMPANY_NAME: device.COMPANY_NAME,
    }),
    [
      device.mac,
      device.name,
      device.type,
      device.location_uuid,
      device.notes,
      device.COMPANY_NAME,
      device.COMPANY_ID,
    ],
  );

  return (
    <tr>
      <td className={styles.tableCol}>{device.name}</td>
      <td className={styles.tableColMac}>{device.mac}</td>
      <td className={styles.tableCol}>
        {editingOptions.includes(device.type) &&
        device.type !== CorpIotTeamDevicesType ? (
          <FormattedMessage
            id={`tabs.device.types.${device.type}`}
            defaultMessage={device.type}
          />
        ) : device.type === CorpIotTeamDevicesType ? (
          <b>Corp IoT / Team Device</b>
        ) : (
          device.type
        )}
      </td>
      <td className={styles.tableCol} style={{ verticalAlign: 'middle' }}>
        <Actions
          props={{ ...popover }}
          onEdit={actions.onEdit}
          onDelete={actions.onDelete}
          onConfirm={actions.onConfirm}
          isConfirmAvailable={
            hasAccessToCorpDevices && device.type === CorpIotTeamDevicesType
          }
        />
        <PopoverDisclosure {...popover} className={styles.menuBtn} />
      </td>
    </tr>
  );
};

export default DeviceTableRow;
